/** Hot counter reports «Готова продукція» web-admin API (MD-0122), administrators only. */

import { api } from '../client';

export type HotReportStatus = 'waiting' | 'posted' | 'review';
export type HotReportLineStatus = 'pending' | 'posted' | 'review';

/** A confirmed production that carries posted lines, and its linked shop transfer. */
export interface HotReportDocument {
  production_id: string;
  transfer_id?: string | null;
}

export interface HotReportSummary {
  id: string;
  /** ISO datetime the chat message was sent. */
  sent_at: string;
  /** `YYYY-MM-DD` from the report header; absent when the header has no usable date. */
  report_date?: string | null;
  destination_key?: string | null;
  destination_name?: string | null;
  status: HotReportStatus;
  /** Why the whole report waits for a person. */
  review_reason?: string | null;
  /** The last failed posting attempt. */
  last_error?: string | null;
  lines_total: number;
  lines_posted: number;
  lines_review: number;
  documents: HotReportDocument[];
  updated_at: string;
}

/** The model's answer for a spelling the server did not know. */
export interface HotReportProposal {
  status: string;
  product_id?: string | null;
  product_name?: string | null;
  reason?: string | null;
}

export interface HotReportLine {
  line_no: number;
  text: string;
  name_key?: string | null;
  /** Decimal strings, kilograms. */
  raw_kg?: string | null;
  cooked_kg?: string | null;
  product_id?: string | null;
  product_name?: string | null;
  /** Who taught the server this spelling: `seed`, `agent` (the model) or `manual`. */
  name_source?: string | null;
  status: HotReportLineStatus;
  reason?: string | null;
  production_id?: string | null;
  transfer_id?: string | null;
  proposal?: HotReportProposal | null;
}

export interface HotReport extends HotReportSummary {
  chat_id: number;
  message_id: number;
  text: string;
  text_hash: string;
  lines: HotReportLine[];
}

export interface HotReportListResponse {
  request_id: string;
  items: HotReportSummary[];
  total: number;
  counts: Record<HotReportStatus, number>;
}

export interface HotReportDetailResponse {
  request_id: string;
  report: HotReport;
}

export interface HotProduct {
  id: string;
  name: string;
}

export interface HotProductsResponse {
  request_id: string;
  items: HotProduct[];
}

export interface HotReportFilters {
  status?: HotReportStatus[];
  limit?: number;
  offset?: number;
}

export interface ResolveHotReportLineRequest {
  /** The line as shown: an edit of the chat message in between refuses the answer. */
  line_text: string;
  product_id?: string;
  raw_kg?: string;
  cooked_kg?: string;
}

const BASE = '/stock/hot-reports';

export const hotReportsApi = {
  list: (filters: HotReportFilters = {}): Promise<HotReportListResponse> =>
    api.webAdmin.get<HotReportListResponse>(BASE, { params: { ...filters } }),

  detail: (id: string): Promise<HotReportDetailResponse> =>
    api.webAdmin.get<HotReportDetailResponse>(`${BASE}/${id}`),

  products: (): Promise<HotProductsResponse> =>
    api.webAdmin.get<HotProductsResponse>(`${BASE}/products`),

  resolveLine: (
    id: string,
    lineNo: number,
    data: ResolveHotReportLineRequest,
  ): Promise<HotReportDetailResponse> =>
    api.webAdmin.post<HotReportDetailResponse>(`${BASE}/${id}/lines/${lineNo}/resolve`, data),

  /** `textHash` is the message text that was compared with the posted documents. */
  acknowledgeChange: (id: string, textHash: string): Promise<HotReportDetailResponse> =>
    api.webAdmin.post<HotReportDetailResponse>(`${BASE}/${id}/acknowledge-change`, {
      text_hash: textHash,
    }),
};
